import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FEATURE_ICONS, FEATURE_ICON_KEYS, MONTHS } from './featureIcons';
import RichTextEditor from './RichTextEditor';
import { Checkbox } from '@/components/ui/checkbox';

const YEARS = [2025, 2026, 2027, 2028];

// Paths must match the real routes in src/App.jsx and src/pages.config.js —
// a stale key here produces a CTA link that goes nowhere.
const PLATFORM_PAGES = [
  { path: '/ausflugsideen', label: 'Ausflugsideen' },
  { path: '/favoriten', label: 'Favoriten' },
  { path: '/meine-touren', label: 'Meine Touren' },
  { path: '/rabatte', label: 'Rabatte' },
  { path: '/magazin', label: 'Magazin' },
  { path: '/funktionen', label: 'Funktionen' },
  { path: '/updates', label: 'Updates' },
  { path: '/aktivitaet-eintragen', label: 'Aktivität eintragen' },
  { path: '/smart-filter', label: 'Smart Filter' },
  { path: '/feedback', label: 'Feedback' },
  { path: '/haeufige-fragen', label: 'Häufige Fragen' },
  { path: '/ueber-uns', label: 'Über uns' },
  { path: '/sponsoring', label: 'Sponsoring' },
  { path: '/partner-werden', label: 'Partner werden' },
  { path: '/cookies', label: 'Cookies' },
  { path: '/datenschutz', label: 'Datenschutz' },
  { path: '/impressum', label: 'Impressum' },
];

export default function FeatureEditDialog({ open, feature, onClose, onSave, allFeatures = [] }) {
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (feature) {
      setDraft({ ...feature });
    } else {
      setDraft({
        slug: '', title_de: '', title_en: '', subtitle_de: '', subtitle_en: '',
        description_de: '', description_en: '', image: '', icon: 'search', update_type: 'neue_funktion',
        cta_label_de: '', cta_label_en: '', cta_link: '',
        new_month: null, new_year: null, sort_order: 0, is_active: true,
      });
    }
  }, [feature, open]);

  const set = (key, value) => setDraft(d => ({ ...d, [key]: value }));

  const handleSave = () => {
    if (!draft.slug || !draft.title_de) return;
    onSave(draft);
    onClose();
  };

  const inputCls = 'w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-emerald-400';
  const labelCls = 'text-gray-500 text-xs mb-1 block';

  // Features available for internal linking (exclude the one being edited)
  const linkableFeatures = allFeatures.filter(f => f.id !== draft.id);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{feature ? 'Update bearbeiten' : 'Neues Update'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className={labelCls}>Slug (URL)</label>
            <Input value={draft.slug || ''} onChange={e => set('slug', e.target.value)} placeholder="z.b. suche-filter" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Titel (DE)</label>
              <Input value={draft.title_de || ''} onChange={e => set('title_de', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Titel (EN)</label>
              <Input value={draft.title_en || ''} onChange={e => set('title_en', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Untertitel (DE)</label>
              <Input value={draft.subtitle_de || ''} onChange={e => set('subtitle_de', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Untertitel (EN)</label>
              <Input value={draft.subtitle_en || ''} onChange={e => set('subtitle_en', e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Beschreibung (DE) — Rich Text</label>
              <RichTextEditor
                value={draft.description_de || ''}
                onChange={html => set('description_de', html)}
                placeholder="Beschreibung auf Deutsch…"
                features={linkableFeatures}
              />
            </div>
            <div>
              <label className={labelCls}>Beschreibung (EN) — Rich Text</label>
              <RichTextEditor
                value={draft.description_en || ''}
                onChange={html => set('description_en', html)}
                placeholder="Description in English…"
                features={linkableFeatures}
              />
            </div>
          </div>
          <div>
            <label className={labelCls}>Titelbild URL</label>
            <Input value={draft.image || ''} onChange={e => set('image', e.target.value)} placeholder="https://..." />
          </div>
          <div>
            <label className={labelCls}>Icon</label>
            <select className={inputCls} value={draft.icon || 'search'} onChange={e => set('icon', e.target.value)}>
              {FEATURE_ICON_KEYS.map(key => (
                <option key={key} value={key}>{FEATURE_ICONS[key].label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Typ des Updates</label>
            <select className={inputCls} value={draft.update_type || 'neue_funktion'} onChange={e => set('update_type', e.target.value)}>
              <option value="neue_funktion">Neue Funktion</option>
              <option value="neuer_inhalt">Neuer Inhalt</option>
            </select>
          </div>

          {/* CTA Button */}
          <div className="border-t pt-4 space-y-3">
            <p className="font-medium text-gray-900 text-sm">Optional: CTA-Button am Seitenende</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Button-Label (DE)</label>
                <Input value={draft.cta_label_de || ''} onChange={e => set('cta_label_de', e.target.value)} placeholder="z.b. Zu den Favoriten" />
              </div>
              <div>
                <label className={labelCls}>Button-Label (EN)</label>
                <Input value={draft.cta_label_en || ''} onChange={e => set('cta_label_en', e.target.value)} placeholder="e.g. Go to Favorites" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Ziel-Seite (interner Plattform-Link)</label>
              <select
                className={inputCls}
                value=""
                onChange={e => {
                  if (e.target.value === '') return;
                  set('cta_link', e.target.value);
                }}
              >
                <option value="">— Schnellauswahl oder unten manuell eingeben —</option>
                <optgroup label="Plattformseiten">
                  {PLATFORM_PAGES.map(p => (
                    <option key={p.path} value={p.path}>{p.label} ({p.path})</option>
                  ))}
                </optgroup>
                {linkableFeatures.length > 0 && (
                  <optgroup label="Update-Unterseiten">
                    {linkableFeatures.map(f => (
                      <option key={f.id} value={`/updates/${f.slug}`}>
                        {f.title_de || f.slug} (/updates/{f.slug})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <Input
                value={draft.cta_link || ''}
                onChange={e => set('cta_link', e.target.value)}
                placeholder="/ausflugsideen?category=…  oder  /updates/slug"
                className="mt-2"
              />
              <p className="text-xs text-gray-400 mt-1">
                Tipp: Vorgefilterte Ausflugsideen z.B. mit <code>/ausflugsideen?category=ID</code>,
                einzelne Aktivitäten mit <code>/&lt;kategorie&gt;/&lt;slug&gt;</code> (z.B. <code>/park/horburgpark-basel</code>).
              </p>
            </div>
          </div>

          {/* Publish checkbox */}
          <div className="border-t pt-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_active"
                checked={draft.is_active ?? true}
                onCheckedChange={(checked) => set('is_active', checked)}
              />
              <label htmlFor="is_active" className="text-sm cursor-pointer">
                Veröffentlicht
              </label>
            </div>
            <p className="text-xs text-gray-400 mt-1 ml-6">
              {draft.is_active
                ? 'Sichtbar für alle Besucher auf der Updates-Seite.'
                : 'Entwurf — nur für Admins sichtbar.'}
            </p>
          </div>

          <div className="border-t pt-4">
            <p className="font-medium text-gray-900 text-sm mb-1">Erscheinungsdatum</p>
            <p className="text-xs text-gray-400 mb-3">Das Erscheinungsdatum wird auf der Update-Karte und der Detailseite angezeigt.</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Monat</label>
                <select className={inputCls} value={draft.new_month || ''} onChange={e => set('new_month', e.target.value ? parseInt(e.target.value) : null)}>
                  <option value="">—</option>
                  {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Jahr</label>
                <select className={inputCls} value={draft.new_year || ''} onChange={e => set('new_year', e.target.value ? parseInt(e.target.value) : null)}>
                  <option value="">—</option>
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave}>
            {draft.is_active ? 'Veröffentlichen' : 'Als Entwurf speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}